import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Receipt, ArrowClockwise, Plus, Download, X, Wallet, ChartLine,
  MagnifyingGlass, PencilSimple, Trash, Link as LinkIcon, FileXls,
} from '@phosphor-icons/react';
import { ReceiptDialog, ReceiptSourceBadge } from '../../components/admin/ReceiptDialog';
import { PaymentLinksTab } from '../../components/admin/PaymentLinksTab';
import { AdvanceAllocationDialog } from '../../components/admin/receipts/AdvanceAllocationDialog';
import { ReconciliationTab } from '../../components/admin/receipts/ReconciliationTab';
import { Scales } from '@phosphor-icons/react';

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const PAYMENT_MODES = ['Cash', 'Card', 'Cheque', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'Bank Transfer', 'E-Transfer', 'Razorpay', 'Other'];
const SOURCES = ['razorpay', 'manual'];
const STATUSES = ['success', 'pending', 'failed', 'cancelled'];
const RECEIPT_TYPES = ['quotation', 'advance', 'adjustment'];

function PaymentReceipts() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'register');
  return (
    <Layout>
      <div className="space-y-6 pb-12" data-testid="receipts-page">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-zinc-950 flex items-center gap-2">
            <Receipt size={30} className="text-orange-500" /> Payment Receipts
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Central Receipt Register — Razorpay auto-receipts, manual receipts, customer advances, and collection reports in one place.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-zinc-200">
          {[
            { k: 'register', label: 'Receipt Register', icon: Receipt },
            { k: 'collection', label: 'Collection Report', icon: ChartLine },
            { k: 'advances', label: 'Customer Advances', icon: Wallet },
            { k: 'reconciliation', label: 'Allocation & Reconciliation', icon: Scales },
            { k: 'links', label: 'Payment Links', icon: LinkIcon },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                data-testid={`receipts-tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
                  tab === t.k ? 'border-orange-500 text-orange-600' : 'border-transparent text-zinc-500 hover:text-zinc-700'
                }`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'register' && <RegisterTab />}
        {tab === 'collection' && <CollectionTab />}
        {tab === 'advances' && <AdvancesTab />}
        {tab === 'reconciliation' && <ReconciliationTab />}
        {tab === 'links' && <PaymentLinksTab />}
      </div>
    </Layout>
  );
}

function RegisterTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [cancelling, setCancelling] = useState(null);   // receipt row
  const [cancelReason, setCancelReason] = useState('');
  const [editing, setEditing] = useState(null);         // receipt row
  const [editForm, setEditForm] = useState({ amount: '', payment_date: '', payment_mode: '', received_bank: '', reference: '', remarks: '' });
  const [deleting, setDeleting] = useState(null);       // receipt row for hard delete
  const [filters, setFilters] = useState({
    date_from: '', date_to: '',
    customer_name: '', customer_mobile: '',
    quotation_no: '', jo_number: '',
    executive_name: '', payment_mode: '',
    payment_source: '', payment_status: '',
    received_bank: '', receipt_type: '',
    allocation_status: '', include_cancelled: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      Object.entries(filters).forEach(([k, v]) => {
        if (v === '' || v === false) return;
        params[k] = v;
      });
      const { data } = await api.get('/api/admin/receipts', { params });
      setRows(data.receipts || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load receipts');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const doExport = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === '' || v === false) return;
      params.append(k, v);
    });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/receipts/export?${params.toString()}`;
    window.open(url, '_blank');
  };

  const submitCancel = async () => {
    if (!cancelling) return;
    try {
      await api.post(`/api/admin/receipts/${cancelling._id}/cancel`, { reason: cancelReason });
      toast.success(`Receipt ${cancelling.receipt_number} cancelled`);
      setCancelling(null); setCancelReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const openEdit = (r) => {
    setEditing(r);
    setEditForm({
      amount: String(r.amount || ''),
      payment_date: r.payment_date || new Date().toISOString().slice(0, 10),
      payment_mode: r.payment_mode || r.mode || 'Cash',
      received_bank: r.received_bank || '',
      reference: r.reference || '',
      remarks: r.remarks || '',
    });
  };

  const submitEdit = async () => {
    if (!editing) return;
    const amt = parseFloat(editForm.amount);
    if (!amt || amt <= 0) { toast.error('Amount must be positive'); return; }
    try {
      await api.put(`/api/admin/receipts/${editing._id}`, {
        amount: amt,
        payment_date: editForm.payment_date,
        payment_mode: editForm.payment_mode,
        received_bank: editForm.received_bank,
        reference: editForm.reference,
        remarks: editForm.remarks,
      });
      toast.success(`Receipt ${editing.receipt_number} updated`);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const submitDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/api/admin/receipts/${deleting._id}`);
      toast.success(`Receipt ${deleting.receipt_number} deleted`);
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const clearFilter = () => setFilters({
    date_from: '', date_to: '', customer_name: '', customer_mobile: '',
    quotation_no: '', jo_number: '', executive_name: '',
    payment_mode: '', payment_source: '', payment_status: '',
    received_bank: '', receipt_type: '', allocation_status: '',
    include_cancelled: false,
  });

  return (
    <>
      <Card className="border-zinc-300 rounded-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <FField label="From Date">
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-from" />
            </FField>
            <FField label="To Date">
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-to" />
            </FField>
            <FField label="Customer">
              <Input value={filters.customer_name} onChange={(e) => setFilters({ ...filters, customer_name: e.target.value })} placeholder="name" data-testid="filter-cust" />
            </FField>
            <FField label="Mobile">
              <Input value={filters.customer_mobile} onChange={(e) => setFilters({ ...filters, customer_mobile: e.target.value })} placeholder="10-digit" data-testid="filter-mobile" />
            </FField>
            <FField label="Quotation No">
              <Input value={filters.quotation_no} onChange={(e) => setFilters({ ...filters, quotation_no: e.target.value })} data-testid="filter-quotation" />
            </FField>
            <FField label="JO Number">
              <Input value={filters.jo_number} onChange={(e) => setFilters({ ...filters, jo_number: e.target.value })} />
            </FField>
            <FField label="Payment Mode">
              <SelectAny value={filters.payment_mode} onValueChange={(v) => setFilters({ ...filters, payment_mode: v })} options={PAYMENT_MODES} testid="filter-mode" />
            </FField>
            <FField label="Source">
              <SelectAny value={filters.payment_source} onValueChange={(v) => setFilters({ ...filters, payment_source: v })} options={SOURCES.map(s => ({ v: s, l: s.toUpperCase() }))} testid="filter-source" />
            </FField>
            <FField label="Status">
              <SelectAny value={filters.payment_status} onValueChange={(v) => setFilters({ ...filters, payment_status: v })} options={STATUSES} testid="filter-status" />
            </FField>
            <FField label="Bank">
              <Input value={filters.received_bank} onChange={(e) => setFilters({ ...filters, received_bank: e.target.value })} data-testid="filter-bank" />
            </FField>
            <FField label="Executive">
              <Input value={filters.executive_name} onChange={(e) => setFilters({ ...filters, executive_name: e.target.value })} data-testid="filter-exec" />
            </FField>
            <FField label="Receipt Type">
              <SelectAny value={filters.receipt_type} onValueChange={(v) => setFilters({ ...filters, receipt_type: v })} options={RECEIPT_TYPES} testid="filter-type" />
            </FField>
          </div>

          <div className="flex flex-wrap gap-2 justify-between items-center mt-4 pt-3 border-t border-zinc-100">
            <label className="flex items-center gap-2 text-xs text-zinc-600">
              <input type="checkbox" checked={filters.include_cancelled} onChange={(e) => setFilters({ ...filters, include_cancelled: e.target.checked })} data-testid="filter-cancelled" />
              Include cancelled receipts
            </label>
            <div className="flex gap-2">
              <Button variant="outline" onClick={clearFilter} data-testid="clear-filter"><X size={14} className="mr-1" /> Clear</Button>
              <Button variant="outline" onClick={load} data-testid="reload"><ArrowClockwise size={14} /></Button>
              <Button variant="outline" onClick={doExport} data-testid="export-btn"><Download size={14} className="mr-1" /> Export CSV</Button>
              <Button onClick={() => setShowForm(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="new-receipt">
                <Plus size={14} className="mr-1" /> New Receipt
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-zinc-300 rounded-sm mt-4">
        <CardContent className="p-0 overflow-x-auto">
          <div className="p-3 text-xs text-zinc-500 border-b border-zinc-100">{total} receipt{total === 1 ? '' : 's'}</div>
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="p-3 text-left">Receipt #</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-left">Mobile</th>
                <th className="p-3 text-left">Quotation</th>
                <th className="p-3 text-left">JO</th>
                <th className="p-3 text-center">Source</th>
                <th className="p-3 text-left">Mode</th>
                <th className="p-3 text-left">Bank / Ref</th>
                <th className="p-3 text-right">Amount</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="p-6 text-center text-zinc-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={12} className="p-6 text-center text-zinc-400">No receipts match your filter.</td></tr>
              ) : rows.map((r) => (
                <tr key={r._id || r.receipt_number} className={`border-b border-zinc-100 hover:bg-zinc-50 ${r.is_cancelled ? 'opacity-50 line-through' : ''}`} data-testid={`receipt-row-${r.receipt_number}`}>
                  <td className="p-3 font-mono text-xs font-bold text-orange-700">{r.receipt_number || '—'}</td>
                  <td className="p-3 text-xs">{r.payment_date || '—'}</td>
                  <td className="p-3">{r.customer_name || '—'}</td>
                  <td className="p-3 font-mono text-xs text-zinc-700">{r.customer_mobile || '—'}</td>
                  <td className="p-3 font-mono text-xs">{r.quotation_no || '—'}</td>
                  <td className="p-3 font-mono text-xs">{r.jo_number || '—'}</td>
                  <td className="p-3 text-center"><ReceiptSourceBadge source={r.payment_source || r.source} /></td>
                  <td className="p-3 text-xs">{r.payment_mode || r.mode || '—'}</td>
                  <td className="p-3 text-xs">
                    <div>{r.received_bank || '—'}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{r.reference || ''}</div>
                  </td>
                  <td className="p-3 text-right font-bold text-emerald-700">{fmtINR(r.amount)}</td>
                  <td className="p-3 text-center">
                    <Badge className={`text-[10px] ${
                      r.is_cancelled ? 'bg-red-100 text-red-800' :
                      r.payment_status === 'success' ? 'bg-green-100 text-green-800' :
                      r.payment_status === 'pending' ? 'bg-amber-100 text-amber-800' :
                      'bg-zinc-200 text-zinc-700'
                    }`}>
                      {(r.is_cancelled ? 'CANCELLED' : (r.payment_status || 'SUCCESS')).toUpperCase()}
                    </Badge>
                  </td>
                  <td className="p-3 text-center">
                    {r.is_cancelled ? (
                      <span className="text-[10px] text-zinc-400">cancelled</span>
                    ) : (r.payment_source || r.source) === 'razorpay' ? (
                      <span className="text-[10px] text-zinc-400">read-only</span>
                    ) : (
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="outline" className="text-blue-600 text-xs h-7 px-2" onClick={() => openEdit(r)} data-testid={`edit-${r.receipt_number}`} title="Edit">
                          <PencilSimple size={12} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-amber-700 text-xs h-7 px-2" onClick={() => setCancelling(r)} data-testid={`cancel-${r.receipt_number}`} title="Soft cancel (keeps audit)">
                          Cancel
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 text-xs h-7 px-2" onClick={() => setDeleting(r)} data-testid={`delete-${r.receipt_number}`} title="Permanent delete">
                          <Trash size={12} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <ReceiptDialog
        open={showForm}
        onOpenChange={setShowForm}
        defaultReceiptType="quotation"
        onCreated={() => load()}
      />

      <Dialog open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Receipt {cancelling?.receipt_number}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-zinc-600">
              Amount: <span className="font-bold text-emerald-700">{fmtINR(cancelling?.amount)}</span> · Customer: {cancelling?.customer_name}
            </div>
            <div className="text-xs text-amber-800 p-2 bg-amber-50 border border-amber-200 rounded-sm">
              This is a soft cancellation — the receipt stays in audit history but no longer reduces the quotation balance or outstanding.
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Reason</Label>
              <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Cheque bounced" data-testid="cancel-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(null)}>Keep Receipt</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submitCancel} data-testid="confirm-cancel">Confirm Cancellation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Receipt {editing?.receipt_number}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-zinc-500 mb-2">
            Customer: <span className="font-mono">{editing?.customer_name} · {editing?.customer_mobile}</span>
            {editing?.quotation_no ? <span> · Quotation: <span className="font-mono">{editing.quotation_no}</span></span> : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Amount*</Label>
              <Input type="number" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} data-testid="edit-amount" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Receipt Date</Label>
              <Input type="date" value={editForm.payment_date} onChange={(e) => setEditForm({ ...editForm, payment_date: e.target.value })} data-testid="edit-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Payment Mode</Label>
              <Select value={editForm.payment_mode} onValueChange={(v) => setEditForm({ ...editForm, payment_mode: v })}>
                <SelectTrigger data-testid="edit-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Received Bank</Label>
              <Input value={editForm.received_bank} onChange={(e) => setEditForm({ ...editForm, received_bank: e.target.value })} data-testid="edit-bank" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Reference / UTR</Label>
              <Input value={editForm.reference} onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })} data-testid="edit-ref" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Remarks</Label>
              <Input value={editForm.remarks} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} data-testid="edit-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={submitEdit} data-testid="edit-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700">Permanently delete {deleting?.receipt_number}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-zinc-600">
              Amount: <span className="font-bold text-emerald-700">{fmtINR(deleting?.amount)}</span> · Customer: {deleting?.customer_name}
            </div>
            <div className="text-xs text-red-800 p-2 bg-red-50 border border-red-200 rounded-sm">
              <strong>Warning:</strong> Hard delete removes the receipt entirely. Outstanding and Collection Report will drop this amount immediately. An audit-log entry is recorded but the receipt cannot be recovered.
              <br />Prefer <strong>Cancel</strong> if you want the row visible for future review.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Keep Receipt</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submitDelete} data-testid="confirm-delete">Yes, Delete Permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CollectionTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ date_from: '', date_to: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      const { data } = await api.get('/api/admin/receipts/summary', { params });
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  if (loading || !summary) return <div className="p-8 text-center text-zinc-400 text-sm">Loading collection report…</div>;

  return (
    <div className="space-y-4">
      <Card className="border-zinc-300 rounded-sm"><CardContent className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <FField label="From Date">
            <Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} data-testid="col-from" />
          </FField>
          <FField label="To Date">
            <Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} data-testid="col-to" />
          </FField>
          <Button variant="outline" onClick={load}><ArrowClockwise size={14} /></Button>
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <MetricCard title="Total Collection" amount={summary.total_collection} accent="text-zinc-900" testid="metric-total" />
        <MetricCard title="Cash" amount={summary.cash_collection} accent="text-amber-700" />
        <MetricCard title="Bank / UPI / NEFT" amount={summary.bank_collection} accent="text-blue-700" />
        <MetricCard title="Razorpay" amount={summary.razorpay_collection} accent="text-purple-700" />
        <MetricCard title="Advances Total" amount={summary.advance_collection} accent="text-emerald-700" />
        <MetricCard title="Pending Allocation" amount={summary.pending_allocation} accent="text-red-700" />
        <MetricCard title="Receipts Count" amount={summary.total_count} accent="text-zinc-700" isCount />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BreakdownCard title="By Executive" rows={summary.by_executive} />
        <BreakdownCard title="By Bank" rows={summary.by_bank} />
        <BreakdownCard title="By Mode" rows={summary.by_mode} />
      </div>

      <BreakdownCard title="By Date" rows={summary.by_date} wide />
    </div>
  );
}

function AdvancesTab() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allocFor, setAllocFor] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      const { data } = await api.get(`/api/admin/receipt-allocation/advances-report?${params}`);
      setRows(data.advances || []);
      setTotals(data.totals || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(''); }, [load]);

  const exportCsv = () => {
    const params = new URLSearchParams({ format: 'csv' });
    if (search) params.set('q', search);
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/receipt-allocation/advances-report?${params}`, '_blank');
  };

  const CONV_BADGE = {
    'Converted': 'bg-emerald-100 text-emerald-800',
    'Partially Converted': 'bg-blue-100 text-blue-800',
    'Draft Quotation Pending': 'bg-amber-100 text-amber-800',
    'No Quotation': 'bg-red-100 text-red-700',
  };

  return (
    <>
      <div className="flex flex-wrap justify-between items-end gap-2">
        <div className="flex items-end gap-2">
          <FField label="Search Customer / Mobile">
            <Input className="h-9 w-56" placeholder="Name or mobile number" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load(search)}
              data-testid="advances-search" />
          </FField>
          <Button variant="outline" className="h-9" onClick={() => load(search)} data-testid="advances-search-btn">Search</Button>
          <Button variant="ghost" className="h-9 text-xs" onClick={() => { setSearch(''); load(''); }}>Clear</Button>
        </div>
        <div className="flex items-center gap-2">
          {totals && (
            <span className="text-xs text-zinc-600">
              {totals.count} advances · <b>{fmtINR(totals.amount)}</b> · Unallocated <b className="text-emerald-700">{fmtINR(totals.unallocated)}</b>
            </span>
          )}
          <Button variant="outline" className="h-9 border-emerald-400 text-emerald-700" onClick={exportCsv} data-testid="advances-export-btn">
            <FileXls size={14} className="mr-1" /> Export Excel
          </Button>
          <Button variant="outline" className="h-9" onClick={() => load(search)}><ArrowClockwise size={14} /></Button>
        </div>
      </div>

      <Card className="border-zinc-300 rounded-sm mt-3"><CardContent className="p-0 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="p-3 text-left">Receipt #</th>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Customer</th>
              <th className="p-3 text-left">Mobile</th>
              <th className="p-3 text-left">Executive</th>
              <th className="p-3 text-left">Mode / Bank</th>
              <th className="p-3 text-right">Amount</th>
              <th className="p-3 text-right">Allocated</th>
              <th className="p-3 text-right">Available</th>
              <th className="p-3 text-center">Status</th>
              <th className="p-3 text-center">Conversion</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="p-6 text-center text-zinc-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={12} className="p-6 text-center text-zinc-400">No advances found.</td></tr>
            ) : rows.map((a) => (
              <tr key={a.receipt_number} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`advance-row-${a.receipt_number}`}>
                <td className="p-3 font-mono text-xs font-bold text-orange-700">{a.receipt_number || '—'}</td>
                <td className="p-3 text-xs">{a.payment_date || '—'}</td>
                <td className="p-3">{a.customer_name || '—'}</td>
                <td className="p-3 font-mono text-xs">{a.customer_mobile}</td>
                <td className="p-3 text-xs">{a.executive_name || '—'}</td>
                <td className="p-3 text-xs">
                  <div>{a.payment_mode || '—'}</div>
                  <div className="text-[10px] text-zinc-500">{a.received_bank || ''}</div>
                </td>
                <td className="p-3 text-right font-bold">{fmtINR(a.amount)}</td>
                <td className="p-3 text-right text-blue-700">{fmtINR(a.allocated_amount)}</td>
                <td className="p-3 text-right text-emerald-700 font-bold">{fmtINR(a.unallocated_amount)}</td>
                <td className="p-3 text-center">
                  <Badge className={`text-[10px] ${
                    a.allocation_status === 'allocated' ? 'bg-zinc-200 text-zinc-700' :
                    a.allocation_status === 'partial' ? 'bg-amber-100 text-amber-800' :
                    'bg-emerald-100 text-emerald-800'
                  }`}>{a.allocation_status?.toUpperCase()}</Badge>
                </td>
                <td className="p-3 text-center">
                  <Badge className={`text-[9px] ${CONV_BADGE[a.conversion_status] || 'bg-zinc-100'}`} title={(a.draft_quotations || []).join(', ')}>
                    {a.conversion_status}
                  </Badge>
                  {a.days_pending != null && a.unallocated_amount > 0 && (
                    <div className="text-[9px] text-zinc-400 mt-0.5">{a.days_pending}d pending</div>
                  )}
                </td>
                <td className="p-3 text-center">
                  <Button size="sm" variant="outline" disabled={a.unallocated_amount <= 0}
                    onClick={() => setAllocFor(a)} data-testid={`alloc-${a.receipt_number}`}>
                    Allocate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      <AdvanceAllocationDialog
        advance={allocFor}
        onOpenChange={(o) => !o && setAllocFor(null)}
        onDone={() => load(search)}
      />
    </>
  );
}

// ============ Small building blocks ============

function FField({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}

function SelectAny({ value, onValueChange, options, testid }) {
  const opts = (options || []).map(o => (typeof o === 'string' ? { v: o, l: o } : o));
  return (
    <Select value={value || 'all'} onValueChange={(v) => onValueChange(v === 'all' ? '' : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="All" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        {opts.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function MetricCard({ title, amount, accent, isCount, testid }) {
  return (
    <Card className="border-zinc-300 rounded-sm"><CardContent className="p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{title}</div>
      <div className={`text-lg font-bold mt-1 ${accent || 'text-zinc-800'}`} data-testid={testid}>
        {isCount ? Number(amount || 0).toLocaleString('en-IN') : fmtINR(amount)}
      </div>
    </CardContent></Card>
  );
}

function BreakdownCard({ title, rows, wide }) {
  return (
    <Card className="border-zinc-300 rounded-sm">
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">{title}</div>
        {(!rows || rows.length === 0) ? (
          <div className="text-sm text-zinc-400">No data</div>
        ) : (
          <div className={`space-y-1 ${wide ? 'grid grid-cols-2 md:grid-cols-4 gap-2 space-y-0' : ''}`}>
            {rows.slice(0, 20).map((row, i) => (
              <div key={i} className="flex justify-between border-b border-zinc-100 pb-1 text-sm">
                <span className="text-zinc-700 truncate max-w-[10rem]">{row.key || '—'}</span>
                <span className="font-mono font-bold text-zinc-900">{fmtINR(row.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PaymentReceipts;
export { PaymentReceipts };
